import type { FileManagerRef } from 'devextreme-react/file-manager';
import dxDataGrid from 'devextreme/ui/data_grid';
import {
  attachFileBrowserGridLayoutWatch,
  captureFileBrowserGridState,
  applyFileBrowserGridStateStoring,
  enforceMandatoryFileBrowserGridColumns,
  FILE_BROWSER_DETAILS_STATE_KEY,
  fileBrowserGridStateKey,
  reapplyFileBrowserGridLayout,
  writeFileBrowserGridState
} from './fileBrowserGridState';
import type { StorageMountCatalog } from './fileBrowserApi';

export { FILE_BROWSER_DETAILS_STATE_KEY, fileBrowserGridStateKey };
export const JARVIS_COLUMN_CHOOSER = 'jarvisColumnChooser';

const configuredGrids = new WeakSet<dxDataGrid>();
const watchedFileManagers = new WeakSet<object>();

const MAX_CONFIGURE_ATTEMPTS = 30;

type FileManagerItemView = {
  _filesView?: dxDataGrid;
  on?: (event: string, handler: (e: { fullName?: string }) => void) => void;
};

type FileManagerInternal = {
  element: () => HTMLElement;
  option: (name: string) => unknown;
  _itemView?: FileManagerItemView;
  on?: (event: string, handler: (e: { fullName?: string }) => void) => void;
};

function isDetailsView(fileManagerRef: { current: FileManagerRef | null }): boolean {
  return fileManagerRef.current?.instance()?.option('itemView.mode') === 'details';
}

function isDataGridInstance(value: unknown): value is dxDataGrid {
  return (
    !!value &&
    typeof value === 'object' &&
    typeof (value as dxDataGrid).option === 'function'
  );
}

function getGridFromDom(root: HTMLElement): dxDataGrid | null {
  const detailsRoot = root.querySelector('.dx-filemanager-details');
  if (!detailsRoot) return null;

  const candidates = [
    detailsRoot.querySelector('.dx-datagrid'),
    detailsRoot.querySelector('.dx-gridbase-container'),
    detailsRoot.firstElementChild
  ];

  for (const candidate of candidates) {
    if (!candidate) continue;
    try {
      const instance = dxDataGrid.getInstance(candidate) as dxDataGrid | undefined;
      if (isDataGridInstance(instance)) return instance;
    } catch {
      // try next candidate
    }
  }

  return null;
}

export function getFileManagerDetailsDataGrid(
  fileManagerRef: { current: FileManagerRef | null }
): dxDataGrid | null {
  const fileManager = fileManagerRef.current?.instance() as FileManagerInternal | undefined;
  if (!fileManager) return null;

  const filesView = fileManager._itemView?._filesView;
  if (isDataGridInstance(filesView)) return filesView;

  const root = fileManager.element();
  if (!root) return null;

  return getGridFromDom(root);
}

function applyColumnOptions(grid: dxDataGrid): void {
  grid.columnOption('fileItem.thumbnail', {
    allowSorting: false,
    allowFiltering: false,
    allowHeaderFiltering: false,
    allowSearch: false,
    allowHiding: true,
    hidingPriority: 0
  });
  grid.columnOption('fileItem.name', {
    allowFiltering: false,
    allowHeaderFiltering: false,
    allowSearch: false,
    allowHiding: false,
    minWidth: 120,
    hidingPriority: 100
  });
  grid.columnOption('fileItem.dataItem.actions', {
    allowSorting: false,
    allowFiltering: false,
    allowHeaderFiltering: false,
    allowHiding: true,
    hidingPriority: 1
  });
  grid.columnOption('fileItem.dataItem.extension', {
    allowFiltering: false,
    allowHeaderFiltering: false,
    allowHiding: true,
    hidingPriority: 2
  });
  grid.columnOption('fileItem.dateModified', {
    allowFiltering: false,
    allowHeaderFiltering: false,
    allowSearch: false,
    allowHiding: true,
    hidingPriority: 3
  });
  grid.columnOption('fileItem.size', {
    allowFiltering: false,
    allowHeaderFiltering: false,
    allowSearch: false,
    allowHiding: true,
    hidingPriority: 4
  });
  grid.columnOption('fileItem.isParentFolder', {
    allowSorting: false,
    allowFiltering: false,
    allowHeaderFiltering: false,
    allowSearch: false,
    allowHiding: false,
    showInColumnChooser: false
  });
}

function enforceFileBrowserGridFilterOptions(grid: dxDataGrid): void {
  grid.option('filterRow', { visible: false });
  grid.option('headerFilter', { visible: false });
}

export function configureFileBrowserDetailsGrid(
  grid: dxDataGrid,
  catalog: StorageMountCatalog = 'all'
): boolean {
  const isNewGrid = !configuredGrids.has(grid);
  if (isNewGrid) {
    configuredGrids.add(grid);

    grid.beginUpdate();
    try {
      grid.option({
        showColumnHeaders: true,
        allowColumnReordering: true,
        allowColumnResizing: true,
        columnResizingMode: 'widget',
        columnHidingEnabled: true,
        columnChooser: { enabled: false },
        filterRow: { visible: false },
        headerFilter: { visible: false }
      });

      applyColumnOptions(grid);
      applyFileBrowserGridStateStoring(grid, catalog);
      attachFileBrowserGridLayoutWatch(grid, catalog);
    } finally {
      grid.endUpdate();
    }
  }

  reapplyFileBrowserGridLayout(grid, catalog);
  enforceFileBrowserGridFilterOptions(grid);
  enforceMandatoryFileBrowserGridColumns(grid);
  applyColumnOptions(grid);
  grid.updateDimensions();
  return true;
}

export function configureFileBrowserDetailsGridFromRef(
  fileManagerRef: { current: FileManagerRef | null },
  catalog: StorageMountCatalog = 'all'
): boolean {
  if (!isDetailsView(fileManagerRef)) return false;
  const grid = getFileManagerDetailsDataGrid(fileManagerRef);
  if (!grid) return false;
  return configureFileBrowserDetailsGrid(grid, catalog);
}

export function scheduleConfigureFileBrowserDetailsGridFromRef(
  fileManagerRef: { current: FileManagerRef | null },
  catalog: StorageMountCatalog = 'all'
): void {
  ensureFileManagerGridWatch(fileManagerRef, catalog);

  if (!isDetailsView(fileManagerRef)) return;

  let attempts = 0;

  const tryConfigure = () => {
    if (!isDetailsView(fileManagerRef)) return;

    const grid = getFileManagerDetailsDataGrid(fileManagerRef);
    if (grid) {
      configureFileBrowserDetailsGrid(grid, catalog);
      return;
    }

    attempts += 1;
    if (attempts < MAX_CONFIGURE_ATTEMPTS) {
      requestAnimationFrame(tryConfigure);
    }
  };

  tryConfigure();
}

function ensureFileManagerGridWatch(
  fileManagerRef: { current: FileManagerRef | null },
  catalog: StorageMountCatalog = 'all'
): void {
  const fileManager = fileManagerRef.current?.instance() as FileManagerInternal | undefined;
  if (!fileManager || watchedFileManagers.has(fileManager)) return;

  watchedFileManagers.add(fileManager);
  fileManager.on?.('optionChanged', (e) => {
    if (e.fullName === 'itemView.mode') {
      scheduleConfigureFileBrowserDetailsGridFromRef(fileManagerRef, catalog);
    }
  });
}

export function reapplyFileBrowserGridLayoutFromRef(
  fileManagerRef: { current: FileManagerRef | null }
): void {
  const grid = getFileManagerDetailsDataGrid(fileManagerRef);
  if (grid) reapplyFileBrowserGridLayout(grid);
}

export function persistFileBrowserGridState(
  fileManagerRef: { current: FileManagerRef | null },
  catalog: StorageMountCatalog = 'all'
): void {
  const grid = getFileManagerDetailsDataGrid(fileManagerRef);
  if (!grid) return;
  writeFileBrowserGridState(captureFileBrowserGridState(grid), catalog);
}

export function isFileBrowserDetailsView(
  fileManagerRef: { current: FileManagerRef | null }
): boolean {
  return isDetailsView(fileManagerRef);
}
